const CONSULTATION_PRODUCT_PATTERN = /\b(?:testosterone|enclomiphene|retatrutide)\b/i
const LAB_TEXT_PATTERN = /(?:\b(?:lab|labs|laboratory)\b|(?:^|[_-])labs?(?:[_-]|$))/i
const DEFAULT_MAX_AGE_MS = 30 * 60 * 1000
const CLOSED_ORDER_STATUSES = new Set([
  'cancelled',
  'canceled',
  'deleted',
  'refunded',
  'provider_denied',
  'provider_rejected',
  'denied',
  'failed',
])

function objectValue(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null
}

function booleanValue(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1' || normalized === 'yes'
  }
  return false
}

function firstValue(...values) {
  return values.find(value => value !== undefined && value !== null && value !== '')
}

function consultationSource(order = {}) {
  const direct = objectValue(order.postPurchaseConsultation)
  if (direct && Object.keys(direct).length) return direct

  const metadata = objectValue(order.metadata)
  const stored = objectValue(metadata?.post_purchase_consultation)
  return stored && Object.keys(stored).length ? stored : null
}

function scheduledDetails(order = {}, source = {}) {
  const metadata = objectValue(order.metadata) || {}
  const scheduledAt = firstValue(
    source.scheduledAt,
    source.scheduled_at,
    metadata.sync_visit_scheduled_at,
    metadata.syncVisitScheduledAt,
    order.sync_visit_scheduled_at,
    order.syncVisitScheduledAt,
  ) || null
  const visitId = firstValue(
    source.visitId,
    source.visit_id,
    source.syncVisitId,
    source.sync_visit_id,
    metadata.sync_visit_id,
    metadata.syncVisitId,
    metadata.provider_booking_id,
    metadata.kis_rx_provider_booking_id,
    order.sync_visit_id,
    order.syncVisitId,
    order.provider_booking_id,
    order.kis_rx_provider_booking_id,
  )
  const status = String(firstValue(
    source.status,
    source.syncVisitStatus,
    source.sync_visit_status,
    metadata.sync_visit_status,
    metadata.syncVisitStatus,
    order.sync_visit_status,
    order.syncVisitStatus,
  ) || '').trim().toLowerCase()
  const scheduledByStatus = ['scheduled', 'confirmed', 'completed'].includes(status)

  return {
    scheduled: booleanValue(source.scheduled) || Boolean(scheduledAt || visitId || scheduledByStatus),
    scheduledAt,
  }
}

export function postPurchaseConsultationForOrder(order = {}) {
  const resolvedOrder = objectValue(order) || {}
  const orderStatus = String(firstValue(resolvedOrder.status, resolvedOrder.raw_status) || '').trim().toLowerCase()
  if (CLOSED_ORDER_STATUSES.has(orderStatus)) return null
  const source = consultationSource(resolvedOrder)
  if (!source) return null

  const schedule = scheduledDetails(resolvedOrder, source)
  return {
    required: booleanValue(source.required),
    action: String(firstValue(source.action, source.next_action) || ''),
    appointmentTypeId: String(firstValue(
      source.appointmentTypeId,
      source.appointment_type_id,
    ) || ''),
    appointmentTypeName: String(firstValue(
      source.appointmentTypeName,
      source.appointment_type_name,
    ) || ''),
    contactType: String(firstValue(source.contactType, source.contact_type) || ''),
    returningPatient: booleanValue(firstValue(
      source.returningPatient,
      source.returning_patient,
    )),
    scheduled: schedule.scheduled,
    scheduledAt: schedule.scheduledAt,
  }
}

export function consultationBookingPath(order = {}) {
  const resolvedOrder = objectValue(order) || {}
  const consultation = postPurchaseConsultationForOrder(resolvedOrder)
  const orderId = firstValue(resolvedOrder.id, resolvedOrder.orderId, resolvedOrder.order_id)
  if (
    !consultation ||
    consultation.required !== true ||
    consultation.action !== 'book_consultation' ||
    consultation.scheduled !== false ||
    !consultation.appointmentTypeId ||
    !orderId
  ) {
    return null
  }

  return `/visits?order=${encodeURIComponent(String(orderId))}&appointmentType=${encodeURIComponent(consultation.appointmentTypeId)}`
}

export function isConsultationRoutingOrder(order = {}) {
  const resolvedOrder = objectValue(order) || {}
  const consultation = postPurchaseConsultationForOrder(resolvedOrder)
  if (consultation?.required === true) return true

  const metadata = objectValue(resolvedOrder.metadata) || {}
  const product = objectValue(resolvedOrder.product) || {}
  const categoryText = [
    resolvedOrder.order_type,
    resolvedOrder.product_type,
    resolvedOrder.category,
    resolvedOrder.source,
    product.category,
    product.product_type,
    metadata.order_type,
    metadata.product_type,
    metadata.category,
    metadata.product_category,
  ].filter(Boolean).join(' ')
  const productText = [
    product.display_name,
    product.medication_name,
    product.name,
    resolvedOrder.display_name,
    resolvedOrder.product_display_name,
    resolvedOrder.product_full_name,
    resolvedOrder.medication_name,
    resolvedOrder.name,
    resolvedOrder.title,
    metadata.product_name,
    metadata.productName,
    metadata.product_display_name,
    metadata.medication_name,
  ].filter(Boolean).join(' ')

  if (resolvedOrder.lab_panel || LAB_TEXT_PATTERN.test(categoryText) || LAB_TEXT_PATTERN.test(productText)) {
    return false
  }
  return CONSULTATION_PRODUCT_PATTERN.test(productText)
}

export function recentPendingConsultationOrder(
  orders = [],
  { nowMs = Date.now(), maxAgeMs = DEFAULT_MAX_AGE_MS } = {},
) {
  const resolvedNowMs = Number(nowMs)
  const resolvedMaxAgeMs = Number(maxAgeMs)
  if (!Array.isArray(orders) || !Number.isFinite(resolvedNowMs) || !Number.isFinite(resolvedMaxAgeMs)) {
    return null
  }

  return [...orders]
    .sort((left, right) => Date.parse(right?.created_at || '') - Date.parse(left?.created_at || ''))
    .find(order => {
      const createdAtMs = Date.parse(order?.created_at || '')
      if (!Number.isFinite(createdAtMs)) return false
      const ageMs = resolvedNowMs - createdAtMs
      if (ageMs < 0 || ageMs > resolvedMaxAgeMs) return false

      const consultation = postPurchaseConsultationForOrder(order)
      return consultation?.required === true &&
        consultation.scheduled === false &&
        consultation.action === 'book_consultation' &&
        Boolean(consultation.appointmentTypeId)
    }) || null
}
